/* ===========================================================================
   CustomerMobileSearch.jsx  –  full source
   • New “Referral Rewards” button → /customer-report/referral-rewards/:id
   ========================================================================== */
import React, { useState } from 'react';
import CustomerVpaManagerDialog from '../../components/CustomerVpaManagerDialog';
import { usePermissions } from '../../context/PermissionContext';
import {
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

const CustomerMobileSearch = () => {
  const { can } = usePermissions();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* VPA dialog / delete */
  const [vpaDialogOpen, setVpaDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedVpaForDeletion, setSelectedVpaForDeletion] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();

  /* ────────────────────────────────────────────────────────────
   *  Search by mobile
   * ────────────────────────────────────────────────────────── */
  const handleSearch = async () => {
    if (!phoneNumber) {
      setError('Please enter a mobile number.');
      return;
    }

    setLoading(true);
    setError(null);
    setCustomerData(null);

    try {
      const token = sessionStorage.getItem('token');
      const { status, data } = await axios.get(
        `${API_BASE_URL}/customerProfiles/searchByPhone`,
        {
          params: { phoneNumber },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (status === 200 && data) setCustomerData(data);
      else setError('No customer found with the provided mobile number.');
    } catch (err) {
      if (err.response && err.response.status === 404)
        setError('No customer found with the provided mobile number.');
      else setError('An error occurred while searching. Please try again.');
    }
    setLoading(false);
  };

  /* ────────────────────────────────────────────────────────────
   *  Generic navigator
   * ────────────────────────────────────────────────────────── */
  const handleNavigate = (path) => {
    if (customerData && customerData.customerId) {
      navigate(`/customer-report/${path}/${customerData.customerId}`);
    }
  };

  /* ────────────────────────────────────────────────────────────
   *  VPA helpers
   * ────────────────────────────────────────────────────────── */
  const handleOpenVpaDialog  = () => setVpaDialogOpen(true);
  const handleCloseVpaDialog = () => setVpaDialogOpen(false);

  const handleDeleteVpaClick = (vpa) => {
    setSelectedVpaForDeletion(vpa);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedVpaForDeletion || !customerData) return;
    setDeleteLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/customerProfiles/${customerData.customerId}/vpa/${selectedVpaForDeletion._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCustomerData({
        ...customerData,
        customerVPA: customerData.customerVPA.filter(
          (v) => v._id !== selectedVpaForDeletion._id
        ),
      });
      setConfirmDialogOpen(false);
      setSelectedVpaForDeletion(null);
      setError(null);
    } catch (err) {
      console.error('Error deleting VPA:', err);
      setError('Failed to delete VPA. Please try again.');
    }
    setDeleteLoading(false);
  };

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ position: 'relative', padding: 2 }}>
        <BackButton />
        <br /><br />

        {/* Title & search */}
        <Typography variant="h5" align="center" gutterBottom>
          Mobile Search
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Mobile Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Search'}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Typography variant="body1" color="error" align="center">
            {error}
          </Typography>
        )}

        {/* Results */}
        {customerData && (
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6">Customer Details</Typography>
            <Typography variant="body1"><strong>Name:</strong> {customerData.customerName}</Typography>
            <Typography variant="body1"><strong>Email:</strong> {customerData.customerEmail}</Typography>
            <Typography variant="body1"><strong>Mobile:</strong> {customerData.customerMobile}</Typography>
            <Typography variant="body1"><strong>Pincode:</strong> {customerData.customerPincode}</Typography>
            <Typography variant="body1"><strong>District:</strong> {customerData.customerDistrictName}</Typography>
            <Typography variant="body1"><strong>Constituency:</strong> {customerData.customerTalukName}</Typography>
            <Typography variant="body1"><strong>Status:</strong> {customerData.customerStatus}</Typography>
            <Typography variant="body1">
              <strong>VPAs:</strong>{' '}
              {customerData.customerVPA.map((v) => v.vpa).join(', ')}
            </Typography>

            {/* Action buttons */}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" onClick={() => handleNavigate('transactions')}>
                  Transactions
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" onClick={() => handleNavigate('rewards')}>
                  Reward Transactions
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" onClick={() => handleNavigate('balance')}>
                  Reward Balance
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" onClick={() => handleNavigate('games')}>
                  Games
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" onClick={() => handleNavigate('welfare-transactions')}>
                  Welfare Transactions
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" onClick={() => handleNavigate('handle-welfare')}>
                  Handle Welfare
                </Button>
              </Grid>
              {/* NEW – Referral Rewards */}
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={() => handleNavigate('referral-rewards')}
                >
                  Referral Rewards
                </Button>
              </Grid>
              {/* All VPA */}
              <Grid item xs={12} sm={6} md={4}>
                <Button fullWidth variant="contained" color="secondary" onClick={handleOpenVpaDialog}>
                  All VPA
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        <CustomerVpaManagerDialog
          open={vpaDialogOpen}
          onClose={handleCloseVpaDialog}
          customer={customerData}
          onUpdated={(list) => setCustomerData({ ...customerData, customerVPA: list })}
        />
      </Box>
    </div>
  );
};

export default CustomerMobileSearch;
